import type { SupabaseClient } from '@supabase/supabase-js'
import type { SendOtp } from '@/modules/auth/use-cases'
import {
  ClaimSendLimitError,
  EmailLinkedToDifferentAccountError,
  NetworkError,
  OtpSendFailedError,
  OtpSendThrottledError,
  PhoneLinkedToDifferentAccountError,
  UserNotFoundError,
  isOtpSendRefusal,
} from '@/shared/domain'
import type { HttpPost } from '@/shared/infra/http'
import { HttpError, readOtpSendLimitSeconds } from '@/shared/infra/http'
import type { OtpFlow, OtpFlowMemory } from '../otp-flow-memory'
import { classifySendOtpRefusal } from './classify-send-otp-refusal'

const CLIENT_PLATFORM = 'cc_web'

type SendSigninOtpBody =
  | { email: string; platform: typeof CLIENT_PLATFORM }
  | { phone: string; platform: typeof CLIENT_PLATFORM }

type SendSigninOtpResult = { flow: OtpFlow }

type SignInWithOtpCredentials = Parameters<SupabaseClient['auth']['signInWithOtp']>[0]

export class SupabaseSendOtpRepository {
  constructor(
    private readonly client: SupabaseClient,
    private readonly httpClient: HttpPost,
    private readonly flowMemory: OtpFlowMemory,
  ) {}

  execute: SendOtp = async ({ identifier, channel, allowCreate }) => {
    const shouldCreateUser = allowCreate ?? false
    if (channel === 'phone') {
      const flow = await this.consultSigninFlow(
        '/users/send-phone-signin-otp',
        { phone: identifier, platform: CLIENT_PLATFORM },
        'phone',
        () => new PhoneLinkedToDifferentAccountError(),
      )
      this.flowMemory.remember('phone', identifier, flow)
      if (flow === 'link') return
      await this.sendNative({ phone: identifier, options: { shouldCreateUser } })
      return
    }
    const email = identifier.toLowerCase().trim()
    const flow = await this.consultSigninFlow(
      '/users/send-email-signin-otp',
      { email, platform: CLIENT_PLATFORM },
      'email',
      () => new EmailLinkedToDifferentAccountError(),
    )
    this.flowMemory.remember('email', email, flow)
    if (flow === 'link') return
    await this.sendNative({ email: identifier, options: { shouldCreateUser } })
  }

  private async consultSigninFlow(
    url: string,
    body: SendSigninOtpBody,
    channel: 'phone' | 'email',
    conflictError: () => Error,
  ): Promise<OtpFlow> {
    try {
      const result = await this.httpClient.post<SendSigninOtpBody, SendSigninOtpResult | null>({
        url,
        body,
      })
      return result?.flow === 'link' ? 'link' : 'native'
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.status === 409) {
          throw conflictError()
        }
        const retryAfterSeconds = readOtpSendLimitSeconds(err)
        if (retryAfterSeconds !== null) {
          throw new ClaimSendLimitError(channel, retryAfterSeconds)
        }
        if (err.status === 429) {
          throw new OtpSendThrottledError(channel)
        }
        throw new OtpSendFailedError()
      }
      if (err instanceof NetworkError) {
        throw err
      }
      throw new OtpSendFailedError()
    }
  }

  private async sendNative(credentials: SignInWithOtpCredentials): Promise<void> {
    try {
      const { error } = await this.client.auth.signInWithOtp(credentials)
      if (error) {
        if (error.code === 'otp_disabled' || error.code === 'user_not_found') {
          throw new UserNotFoundError()
        }
        const refusal = classifySendOtpRefusal(error, 'phone' in credentials ? 'phone' : 'email')
        if (refusal) {
          throw refusal
        }
        throw error
      }
    } catch (err) {
      if (err instanceof UserNotFoundError || isOtpSendRefusal(err)) {
        throw err
      }
      if (err instanceof TypeError) {
        throw new NetworkError()
      }
      throw err
    }
  }
}
